import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  addEdge,
  Panel,
  getNodesBounds,
  getViewportForBounds,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { toPng } from "html-to-image";
import { IoArrowBack, IoOpenOutline } from "react-icons/io5";
import {
  HiOutlineEllipsisHorizontal,
  HiOutlineArrowLeft,
  HiOutlineArrowUturnLeft,
  HiOutlineArrowDownTray,
} from "react-icons/hi2";
import { authFetch } from "../../HttpClient";
import UserDetailModal from "./UserDetailModal.js";

const CABINET_COLORS = ["#2563eb", "#16a34a", "#5C1A30", "#d97706", "#0891b2", "#7c3aed"];
const getCabinetColor = (cabinetId) =>
  cabinetId == null ? "#9ca3af" : CABINET_COLORS[cabinetId % CABINET_COLORS.length];

/* ======================
   Custom Node
===================== */
function HierarchyNode({ data }) {
  const isAssistant = data.level === 2;
  const isDirection = data.level === 1;
  const showPoste = !isAssistant && !isDirection && data.poste;
  const initials = `${data.name?.[0] || ""}${data.lastName?.[0] || ""}`;

  return (
    <div
      className="relative rounded-xl border border-gray-100 bg-white shadow-sm px-4 py-3"
      style={{ minWidth: 260 }}
    >
      <Handle type="target" position={Position.Top} style={{ background: "#9ca3af" }} />

      <div className="drag-handle absolute top-1.5 left-1.5 text-gray-300 hover:text-gray-500 cursor-move px-1">
        <HiOutlineEllipsisHorizontal size={16} />
      </div>

      {isDirection && (
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#5C1A30] mb-1.5 pl-1">
          Direction
        </div>
      )}
      {isAssistant && (
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#5C1A30] mb-1.5 pl-1">
          Assistant
        </div>
      )}

      <div className="flex items-center gap-2.5 pl-1">
        {!isDirection && (
          <div className="w-9 h-9 rounded-full bg-[#5C1A30] flex items-center justify-center text-white text-xs font-bold uppercase flex-shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">
            {isDirection ? data.label : `${data.name} ${data.lastName}`}
          </p>
          {showPoste && (
            <p className="text-xs text-gray-400 truncate">{data.poste}</p>
          )}
          {data.cabinet && (
            <p
              className="text-xs font-semibold truncate flex items-center gap-1.5 mt-0.5"
              style={{ color: getCabinetColor(data.cabinet.id) }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: getCabinetColor(data.cabinet.id) }}
              />
              {data.cabinet.name}
            </p>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} style={{ background: "#9ca3af" }} />
    </div>
  );
}

/* ======================
   Dagre Layout
===================== */
const nodeWidth = 280;
const nodeHeight = 110;

function getLayoutedElements(nodes, edges, direction = "TB") {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 60 });

  nodes.forEach((node) =>
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  );
  edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const { x, y } = dagreGraph.node(node.id);
      return {
        ...node,
        position: { x: x - nodeWidth / 2, y: y - nodeHeight / 2 },
      };
    }),
    edges,
  };
}

const nodeTypes = { hierarchy: HierarchyNode };

/* ======================
   Composant Principal
===================== */
export default function Hierarchie({ onCancel }) {
  const [hierarchyData, setHierarchyData] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const flowWrapperRef = useRef(null);

  const fetchHierarchy = async () => {
    try {
      const [hierarchyRes, usersRes] = await Promise.all([
        authFetch(
          "https://backend-myalfa.vercel.app/api/users/hierarchy/global"
        ).then((r) => r.json()),
        authFetch("https://backend-myalfa.vercel.app/api/users").then((r) =>
          r.json()
        ),
      ]);
      if (hierarchyRes.length > 0) setHierarchyData(hierarchyRes[0]);
      setUsersList(usersRes || []);
      setLoading(false);
    } catch (err) {
      console.error("Erreur API :", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  /* ======================
     Construire nodes / edges
  ====================== */
  const buildTree = useCallback(() => {
    if (!hierarchyData) return;

    const findCabinet = (userId) =>
      usersList.find((u) => u.id === userId)?.userCabinets?.[0]?.cabinet || null;

    const hierarchyNodes = [];
    const hierarchyEdges = [];

    const rootId = "direction";
    const level1Users = hierarchyData.children || [];
    const assistants = level1Users.filter((u) => u.level === 2);
    const otherChildren = level1Users.filter((u) => u.level !== 2);
    const templateChild = otherChildren[0];

    // Root Node
    hierarchyNodes.push({
      id: rootId,
      type: "hierarchy",
      dragHandle: ".drag-handle",
      data: {
        label: level1Users.map((u) => `${u.name} ${u.lastName}`).join(" - "),
        level: 1,
      },
      userId: hierarchyData.id,
    });

    // Assistants
    assistants.forEach((a, index) => {
      const id = `assistant-${index}`;
      hierarchyNodes.push({
        id,
        type: "hierarchy",
        dragHandle: ".drag-handle",
        data: {
          name: a.name,
          lastName: a.lastName,
          level: a.level,
          poste: a.poste,
          cabinet: findCabinet(a.id),
        },
        userId: a.id,
      });

      hierarchyEdges.push({
        id: `${rootId}-${id}`,
        source: rootId,
        target: id,
        type: "smoothstep",
        style: { strokeWidth: 1.5, stroke: "#d1c7bd" },
      });
    });

    // Recursive children
    const buildChildren = (user, parentId) => {
      user.children?.forEach((child, index) => {
        const id = `${parentId}-${index}-${child.id}`;

        hierarchyNodes.push({
          id,
          type: "hierarchy",
          dragHandle: ".drag-handle",
          data: {
            name: child.name,
            lastName: child.lastName,
            level: child.level,
            poste: child.poste,
            cabinet: findCabinet(child.id),
          },
          userId: child.id,
        });

        hierarchyEdges.push({
          id: `${parentId}-${id}`,
          source: parentId,
          target: id,
          type: "smoothstep",
          style: { strokeWidth: 1.5, stroke: "#d1c7bd" },
        });

        buildChildren(child, id);
      });
    };

    if (templateChild) buildChildren(templateChild, rootId);

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      hierarchyNodes,
      hierarchyEdges
    );

    const maxY = Math.max(...layoutedNodes.map((n) => n.position.y));

    const hierarchyUserIds = hierarchyNodes.map((n) => n.userId);
    const freeUsers = usersList.filter((u) => !hierarchyUserIds.includes(u.id));

    let offsetX = 0;
    const freeUsersNodes = freeUsers.map((user) => {
      const id = `free-${user.id}`;
      const node = {
        id,
        type: "hierarchy",
        dragHandle: ".drag-handle",
        data: {
          name: user.name,
          lastName: user.lastName,
          level: 3,
          poste: user.poste,
          cabinet: findCabinet(user.id),
        },
        userId: user.id,
        position: {
          x: offsetX,
          y: maxY + 220,
        },
      };
      offsetX += 320;
      return node;
    });

    setNodes([...layoutedNodes, ...freeUsersNodes]);
    setEdges(layoutedEdges);
  }, [hierarchyData, usersList, setEdges, setNodes]);

  useEffect(() => {
    buildTree();
  }, [buildTree]);

  /* ======================
     Connecter / supprimer edges
  ====================== */
  const onConnectHandler = async (params) => {
    setUpdating(true);

    setEdges((eds) => addEdge(params, eds));

    const childNode = nodes.find((n) => n.id === params.target);
    const parentNode = nodes.find((n) => n.id === params.source);
    if (!childNode || !parentNode) {
      setUpdating(false);
      return;
    }

    const level = parentNode.data.level + 1;

    try {
      await authFetch(
        `https://backend-myalfa.vercel.app/api/users/${childNode.userId}/parent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parentId: parentNode.userId, level }),
        }
      );

      await fetchHierarchy();
    } catch (err) {
      console.error("Erreur ajout parent :", err);
    }

    setUpdating(false);
  };

  const deleteEdge = async (edge) => {
    setUpdating(true);

    const childNode = nodes.find((n) => n.id === edge.target);
    const parentNode = nodes.find((n) => n.id === edge.source);
    if (!childNode || !parentNode) {
      setUpdating(false);
      return;
    }

    try {
      await authFetch(
        `https://backend-myalfa.vercel.app/api/users/${childNode.userId}/parent/${parentNode.userId}`,
        { method: "DELETE" }
      );

      await fetchHierarchy();
      setSelectedEdge(null);
    } catch (err) {
      console.error("Erreur suppression relation :", err);
    }

    setUpdating(false);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsersList((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u))
    );
  };

  const handleExportPng = useCallback(() => {
    const viewportEl = flowWrapperRef.current?.querySelector(".react-flow__viewport");
    if (!viewportEl) return;

    const bounds = getNodesBounds(nodes);
    const imageWidth = bounds.width + 100;
    const imageHeight = bounds.height + 100;
    const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.5, 2);

    toPng(viewportEl, {
      backgroundColor: "#ffffff",
      width: imageWidth,
      height: imageHeight,
      style: {
        width: imageWidth,
        height: imageHeight,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    }).then((dataUrl) => {
      const link = document.createElement("a");
      link.download = "organigramme.png";
      link.href = dataUrl;
      link.click();
    });
  }, [nodes]);

  const selectedUser = selectedUserId
    ? usersList.find((u) => u.id === selectedUserId)
    : null;

  if (loading)
    return (
      <div className="flex items-center justify-center h-96 text-slate-500">
        Chargement…
      </div>
    );
  if (!hierarchyData)
    return (
      <div className="flex items-center justify-center h-96 text-slate-500">
        Aucun utilisateur trouvé
      </div>
    );

  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 hover:text-[#840040] transition-colors"
        >
          <IoArrowBack size={14} />
          Liste des collaborateurs
        </button>
        <span>/</span>
        <span className="font-semibold text-gray-700">Organigramme</span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">Organigramme</h1>
      <p className="text-sm text-gray-400 mb-4">
        Structure hiérarchique du groupe ALFA — glissez une carte sur une autre pour
        modifier le rapport hiérarchique.
      </p>

      <div className="flex items-center gap-6 bg-[#fff6ee] text-xs text-gray-600 font-medium rounded-xl px-4 py-2.5 mb-4">
        <span className="flex items-center gap-1.5">
          <HiOutlineEllipsisHorizontal size={15} className="text-gray-400" />
          Glissez la poignée pour déplacer une carte
        </span>
        <span className="flex items-center gap-1.5">
          <HiOutlineArrowLeft size={14} className="text-gray-400" />
          Déposez sur un supérieur pour rattacher
        </span>
        <span className="flex items-center gap-1.5">
          <IoOpenOutline size={14} className="text-gray-400" />
          Double-clic pour ouvrir la fiche
        </span>
      </div>

      <div
        ref={flowWrapperRef}
        className="w-full h-[850px] bg-white rounded-2xl border border-gray-100 relative"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnectHandler}
          onEdgeClick={(event, edge) => setSelectedEdge(edge)}
          onNodeDoubleClick={(event, node) => {
            if (node.userId) setSelectedUserId(node.userId);
          }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable
          zoomOnScroll
        >
          <Background color="#e7ded2" gap={20} size={1} />
          <Controls />

          <Panel position="top-right" className="flex items-center gap-2">
            <button
              onClick={buildTree}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <HiOutlineArrowUturnLeft size={14} />
              Réinitialiser
            </button>
            <button
              onClick={handleExportPng}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <HiOutlineArrowDownTray size={14} />
              Exporter PNG
            </button>
          </Panel>
        </ReactFlow>

        {selectedEdge && (
          <button
            className="absolute top-2 left-2 bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow"
            onClick={() => deleteEdge(selectedEdge)}
          >
            Supprimer la liaison
          </button>
        )}

        {updating && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#840040] border-t-transparent"></div>
              <p className="text-sm text-slate-700 font-medium">
                Mise à jour de l'organigramme en cours...
              </p>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUserId(null)}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}
